import { baseUrl_SPB } from '../BaseUrl';
import FetchLocationsService from '../roster/FetchLocationsService';
import FetchEmployeesService from '../roster/FetchEmployeesService';

/**
 * Fetches employees by companyId only (no locationId) to capture CMANAGER/MANAGER users.
 */
async function fetchEmployeesByCompanyOnly(token, companyId) {
  try {
    const response = await fetch(
      `${baseUrl_SPB}/api/v1/user/getemployees?companyId=${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Fetches all employees across all locations for the current user's company.
 * Uses the same proven /api/v1/user/getemployees endpoint as the Roster.
 */
export default async function FetchAllEmployeesByCompany(token) {
  try {
    // Get all company+location groups
    const locationGroups = await FetchLocationsService(token);
    if (!Array.isArray(locationGroups) || locationGroups.length === 0) return [];

    // Flatten all locations with their companyId attached
    const allLocations = locationGroups.flatMap((company) =>
      (Array.isArray(company.locations) ? company.locations : []).map((loc) => ({
        ...loc,
        companyId: company.id,
      }))
    );

    if (allLocations.length === 0) return [];

    const companyIds = [...new Set(locationGroups.map((c) => c.id).filter(Boolean))];

    // Fetch employees for every location in parallel, then deduplicate by id
    const [locationResults, companyResults] = await Promise.all([
      Promise.all(allLocations.map((loc) => FetchEmployeesService(token, loc).catch(() => null))),
      Promise.all(companyIds.map((cid) => fetchEmployeesByCompanyOnly(token, cid))),
    ]);

    const seen = new Set();
    const employees = [];
    const allBatches = [...locationResults, ...companyResults];
    for (const batch of allBatches) {
      if (!Array.isArray(batch)) continue;
      for (const emp of batch) {
        const key = emp.id || emp.email;
        if (key && !seen.has(key)) {
          seen.add(key);
          employees.push(emp);
        }
      }
    }
    return employees;
  } catch (error) {
    console.error('Error fetching all employees:', error);
    return [];
  }
}
